import React from 'react';
import { render, screen } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';
import ManagementLayout from 'components/layouts/ManagementLayout';

const WIDE_VIEWPORT = '(min-width: 1280px)';
const NARROW_VIEWPORT = '(max-width: 449px)';

// jsdom can't resize, and KeyDS LeftNav reads the viewport through matchMedia to
// choose its expanded state, so the breakpoint has to be faked per test.
function mockViewport(matchingQuery) {
  window.matchMedia = (query) => ({
    matches: query === matchingQuery,
    media: query,
    onchange: null,
    addListener: () => {},
    removeListener: () => {},
    addEventListener: () => {},
    removeEventListener: () => {},
    dispatchEvent: () => false,
  });
}

const renderLayout = (props = {}) =>
  render(
    <ConfigProvider>
      <ManagementLayout currentPath="/management/users" {...props}>
        <p>page content</p>
      </ManagementLayout>
    </ConfigProvider>,
  );

describe('ManagementLayout', () => {
  beforeEach(() => {
    window.localStorage.clear();
    mockViewport(WIDE_VIEWPORT);
  });

  it('renders the page content it wraps', () => {
    renderLayout();

    expect(screen.getByText('page content')).toBeInTheDocument();
  });

  it('renders the Users nav item linking to /management/users', () => {
    renderLayout();

    const link = screen.getByRole('link', { name: /users/i });
    expect(link).toHaveAttribute('href', '/management/users');
  });

  it('shows the brand name in the top nav', () => {
    renderLayout({ brandName: 'User Management' });

    expect(screen.getByText('User Management')).toBeInTheDocument();
  });

  describe('top nav', () => {
    const CURRENT_USER = {
      email: 'mtapajos@decisiv.com',
      logoutPath: '/users/logout',
      name: 'Marcos Tapajos',
      profilePath: '/users/edit/374065',
      roleLabel: 'Global Admin',
    };

    const APP_LINKS = [
      {
        id: 'portal-DCV1',
        kind: 'portal',
        label: 'DECISIV PORTAL',
        url: 'http://portal.dcv.decisiv.test',
      },
    ];

    it('renders the user menu when a current user is supplied', () => {
      renderLayout({ currentUser: CURRENT_USER });

      expect(
        screen.getByRole('button', { name: /Marcos Tapajos/i }),
      ).toBeInTheDocument();
    });

    it('renders the app switcher when links are supplied', () => {
      renderLayout({ appLinks: APP_LINKS });

      expect(
        screen.getByRole('button', { name: 'Show Apps links' }),
      ).toBeInTheDocument();
    });

    it('omits both when the props are absent', () => {
      renderLayout();

      expect(
        screen.queryByRole('button', { name: 'Show Apps links' }),
      ).not.toBeInTheDocument();
      expect(
        screen.queryByRole('button', { name: /Marcos Tapajos/i }),
      ).not.toBeInTheDocument();
    });
  });

  describe('footer', () => {
    const FOOTER = {
      copyright: 'Copyright © 2026 Decisiv, Inc.',
      links: [
        {
          label: 'Privacy Policy',
          url: 'https://www.decisiv.com/privacy-policy',
        },
      ],
      version: 'Version 1.2.3',
    };

    it('renders beneath the nav when expanded', () => {
      renderLayout({ footer: FOOTER });

      expect(
        screen.getByText('Copyright © 2026 Decisiv, Inc.'),
      ).toBeInTheDocument();
      expect(
        screen.getByRole('link', { name: 'Privacy Policy' }),
      ).toBeInTheDocument();
    });

    it('is hidden while the nav is collapsed', () => {
      // There is no room for it beside the icon rail.
      window.localStorage.setItem('management.navExpandedState', 'COLLAPSED_X');

      renderLayout({ footer: FOOTER });

      expect(
        screen.queryByText('Copyright © 2026 Decisiv, Inc.'),
      ).not.toBeInTheDocument();
    });
  });

  describe('nav expanded state', () => {
    it('expands by default on a wide viewport', () => {
      renderLayout();

      expect(screen.getByRole('link', { name: /users/i })).toBeVisible();
    });

    it('reads the stored preference instead of the viewport', () => {
      // A narrow viewport would collapse the nav, but a stored EXPANDED
      // preference must win — that is the point of useStickyLeftNav, and portal
      // navigates with full page loads so the choice has to survive remounts.
      mockViewport(NARROW_VIEWPORT);
      window.localStorage.setItem('management.navExpandedState', 'EXPANDED');

      renderLayout();

      expect(screen.getByRole('link', { name: /users/i })).toBeVisible();
    });

    it('survives a remount via localStorage', () => {
      window.localStorage.setItem('management.navExpandedState', 'COLLAPSED_X');

      const { unmount } = renderLayout();
      unmount();
      renderLayout();

      expect(
        window.localStorage.getItem('management.navExpandedState'),
      ).toEqual('COLLAPSED_X');
    });
  });
});
